import { createPortal } from 'react-dom'
import { useEffect, useRef, useState, useCallback } from 'react'
import { X, Plus, Search, Check } from 'lucide-react'
import { ColorField, Field, editorInput, editorMenu } from './editorUi'
import { useLanguage } from '@/contexts/LanguageContext'

export interface AssignableMember {
  id: string
  name: string
  avatar_url: string
}

export interface AssignableSquad {
  /** Kadro takımının kimliği: ad ve oyuncular ekrana çizilirken kadrodan okunur. */
  id?: string
  name: string
  color: string
}

/** Seçici satırı: bağlı maçın kadro takımı ya da eski hazır liste. */
export interface SquadOption extends AssignableSquad {
  players?: { id: string; name: string }[]
}

export interface AssignableFields {
  title: string
  text: string
  color: string
  squads: AssignableSquad[]
  members: AssignableMember[]
}

interface MemberOption {
  id: string
  username: string
  display_name: string
  avatar_url: string
  team: string
}

interface Props {
  fields: AssignableFields
  onFieldsChange: (patch: Partial<AssignableFields>) => void
  squadPresets?: SquadOption[]
}

export default function AssignablePropertiesFields({ fields, onFieldsChange, squadPresets }: Props) {
  const { T } = useLanguage()
  const [memberSearch, setMemberSearch] = useState('')
  const [memberOptions, setMemberOptions] = useState<MemberOption[]>([])
  const [showMemberDropdown, setShowMemberDropdown] = useState(false)
  const [showSquadDropdown, setShowSquadDropdown] = useState(false)
  const [squadInput, setSquadInput] = useState('')
  const memberInputRef = useRef<HTMLInputElement>(null)
  const squadDropdownRef = useRef<HTMLButtonElement>(null)
  const [memberDropdownPos, setMemberDropdownPos] = useState({ top: 0, left: 0, width: 0 })
  const [squadDropdownPos, setSquadDropdownPos] = useState({ top: 0, left: 0, width: 0 })

  useEffect(() => {
    fetch('/api/members', { credentials: 'include' })
      .then(r => r.ok ? r.json() : [])
      .then((data: MemberOption[]) => setMemberOptions(data))
      .catch(() => {})
  }, [])

  const updateMemberDropdownPos = useCallback(() => {
    if (!memberInputRef.current) return
    const rect = memberInputRef.current.getBoundingClientRect()
    const dropdownHeight = 280
    const flipUp = rect.bottom + dropdownHeight > window.innerHeight
    setMemberDropdownPos({
      top: flipUp ? rect.top - dropdownHeight - 4 : rect.bottom + 4,
      left: rect.left,
      width: Math.max(rect.width, 220),
    })
  }, [])

  const updateSquadDropdownPos = useCallback(() => {
    if (!squadDropdownRef.current) return
    const rect = squadDropdownRef.current.getBoundingClientRect()
    const dropdownHeight = 240
    const flipUp = rect.bottom + dropdownHeight > window.innerHeight
    setSquadDropdownPos({
      top: flipUp ? rect.top - dropdownHeight - 4 : rect.bottom + 4,
      left: rect.left,
      width: Math.max(rect.width, 180),
    })
  }, [])

  useEffect(() => {
    if (showMemberDropdown) updateMemberDropdownPos()
  }, [showMemberDropdown, updateMemberDropdownPos])

  useEffect(() => {
    if (showSquadDropdown) updateSquadDropdownPos()
  }, [showSquadDropdown, updateSquadDropdownPos])

  useEffect(() => {
    if (!showMemberDropdown) return
    const handleClick = (e: MouseEvent) => {
      const target = e.target as Node
      if (memberInputRef.current?.contains(target)) return
      if ((target as HTMLElement).closest?.('[data-member-dropdown]')) return
      setShowMemberDropdown(false)
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [showMemberDropdown])

  useEffect(() => {
    if (!showSquadDropdown) return
    const handleClick = (e: MouseEvent) => {
      const target = e.target as Node
      if (squadDropdownRef.current?.contains(target)) return
      if ((target as HTMLElement).closest?.('[data-squad-dropdown]')) return
      setShowSquadDropdown(false)
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [showSquadDropdown])

  const filteredMembers = memberOptions.filter(m =>
    !fields.members.some(sm => sm.id === m.id) &&
    (m.display_name.toLowerCase().includes(memberSearch.toLowerCase()) ||
     m.username.toLowerCase().includes(memberSearch.toLowerCase()))
  )

  const handleAddMember = (m: MemberOption) => {
    onFieldsChange({
      members: [...fields.members, { id: m.id, name: m.display_name || m.username, avatar_url: m.avatar_url }],
    })
    setMemberSearch('')
  }

  const handleRemoveMember = (id: string) => {
    onFieldsChange({ members: fields.members.filter(m => m.id !== id) })
  }

  const handleAddSquad = () => {
    const trimmed = squadInput.trim()
    if (trimmed && !fields.squads.some(s => s.name === trimmed)) {
      const newSquad = { name: trimmed, color: fields.color }
      const isFirst = fields.squads.length === 0
      onFieldsChange({
        squads: [...fields.squads, newSquad],
        ...(isFirst ? { color: newSquad.color } : {}),
      })
    }
    setSquadInput('')
  }

  const handleRemoveSquad = (squad: AssignableSquad) => {
    onFieldsChange({ squads: fields.squads.filter(s => s !== squad) })
  }

  // Kadro takımının adı değişebilir, kimliği değişmez.
  const isSameSquad = (s: AssignableSquad, option: SquadOption) =>
    option.id ? s.id === option.id : s.name === option.name

  const handleToggleSquadPreset = (preset: SquadOption) => {
    const existing = fields.squads.find(s => isSameSquad(s, preset))
    if (existing) {
      onFieldsChange({ squads: fields.squads.filter(s => s !== existing) })
    } else {
      const isFirst = fields.squads.length === 0
      // Oyuncular kayda yazılmaz: kadro değişince eskimesinler.
      const assigned: AssignableSquad = { name: preset.name, color: preset.color }
      if (preset.id) assigned.id = preset.id
      onFieldsChange({
        squads: [...fields.squads, assigned],
        ...(isFirst ? { color: preset.color } : {}),
      })
    }
  }

  return (
    <>
      {/*
        Renk — **takım atanmışsa takımın rengi öne çıkar.** Bir okun ya da
        simgenin rengi çoğu zaman kimin yürüdüğünü söyler; o bilinince dokuz
        renklik palet ikinci sorudur.
      */}
      {fields.squads.length > 0 ? (
        <Field label={T.strategy.color}>
          <div className="mb-2 flex flex-wrap gap-1.5">
            {fields.squads.map(squad => (
              <button
                key={squad.name}
                onClick={() => onFieldsChange({ color: squad.color })}
                title={squad.name}
                className={`flex h-7 cursor-pointer items-center gap-1.5 rounded-sm border px-2 transition-colors ${
                  fields.color === squad.color ? 'border-white' : 'border-white/15 hover:border-white/50'
                }`}
                style={{ backgroundColor: squad.color }}
              >
                <span className="max-w-[60px] truncate text-[9px] font-semibold text-white/90">{squad.name}</span>
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <label className="relative cursor-pointer">
              <input
                type="color" value={fields.color}
                onChange={e => onFieldsChange({ color: e.target.value })}
                className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
              />
              <div
                className="h-7 w-10 rounded-sm border border-white/15 transition-colors hover:border-white/50"
                style={{ backgroundColor: fields.color }}
              />
            </label>
            <span className="text-[10px] text-muted-foreground/70">{T.strategy.custom}</span>
          </div>
        </Field>
      ) : (
        <ColorField
          label={T.strategy.color}
          custom={T.strategy.custom}
          value={fields.color}
          onChange={v => onFieldsChange({ color: v })}
        />
      )}

      {/* Takımlar */}
      <Field label={T.strategy.squads}>
        <div className="mb-2 flex flex-wrap gap-1">
          {fields.squads.map(squad => (
            <span
              key={squad.id ?? squad.name}
              className="inline-flex items-center gap-1 rounded-sm px-2 py-0.5 text-xs font-medium text-white"
              style={{ backgroundColor: squad.color + '99' }}
            >
              {squad.name}
              <button
                onClick={() => handleRemoveSquad(squad)}
                className="hover:bg-white/20 rounded-full p-0.5 transition-colors"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
        {/* Atanan takımın oyuncuları: kadrodan gelir, buradan düzenlenmez. */}
        {fields.squads.map(squad => {
          const players = squadPresets?.find(o => isSameSquad(squad, o))?.players
          if (!players?.length) return null
          return (
            <p key={squad.id ?? squad.name} className="mb-2 text-[10px] leading-snug text-muted-foreground">
              <span className="font-semibold text-foreground/80">{squad.name}: </span>
              {players.map(p => p.name).join(', ')}
            </p>
          )
        })}
        {/* Kadro takımları seçilir; listede olmayan bir takım (ikinci grup, dost klan)
            her zaman elle de yazılabilir. */}
        {squadPresets && squadPresets.length > 0 && (
          <button
            ref={squadDropdownRef}
            onClick={() => { setShowSquadDropdown(v => !v); updateSquadDropdownPos() }}
            className="mb-1 h-8 w-full cursor-pointer rounded-sm border border-white/10 bg-white/5 px-2 text-left text-xs text-muted-foreground transition-colors hover:border-primary hover:text-foreground"
          >
            {T.strategy.selectSquad}
          </button>
        )}
        <div className="flex gap-1">
          <input
            type="text"
            value={squadInput}
            onChange={e => setSquadInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAddSquad() } }}
            placeholder={T.strategy.addSquad}
            className={editorInput}
          />
          <button
            onClick={handleAddSquad}
            className="flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-sm border border-white/10 bg-white/5 text-muted-foreground transition-colors hover:bg-white/10 hover:text-foreground"
          >
            <Plus className="h-3.5 w-3.5" />
          </button>
        </div>
      </Field>

      {/* Üyeler */}
      <Field label={T.strategy.members}>
        <div className="mb-2 flex flex-wrap gap-1">
          {fields.members.map(member => (
            <div
              key={member.id}
              className="inline-flex items-center gap-1.5 rounded-sm border border-white/10 bg-white/5 px-1.5 py-0.5 text-xs"
            >
              <img
                src={member.avatar_url}
                alt={member.name}
                className="h-5 w-5 rounded-full"
                onError={e => { (e.target as HTMLImageElement).src = '/default-avatar.png' }}
              />
              <span className="max-w-[80px] truncate">{member.name}</span>
              <button
                onClick={() => handleRemoveMember(member.id)}
                className="hover:bg-white/20 rounded-full p-0.5 transition-colors"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
        <div className="flex items-center gap-1">
          <div className="relative flex-1">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-foreground/40" />
            <input
              ref={memberInputRef}
              type="text"
              value={memberSearch}
              onChange={e => { setMemberSearch(e.target.value); if (!showMemberDropdown) setShowMemberDropdown(true); updateMemberDropdownPos() }}
              onFocus={() => { setShowMemberDropdown(true); updateMemberDropdownPos() }}
              placeholder={T.strategy.searchMember}
              className={`${editorInput} pl-7`}
            />
          </div>
        </div>
      </Field>

      {/* Başlık */}
      <Field label={T.strategy.title}>
        <input
          type="text"
          value={fields.title}
          onChange={e => onFieldsChange({ title: e.target.value })}
          placeholder={T.strategy.titlePlaceholder}
          className={editorInput}
        />
      </Field>

      {/* Açıklama */}
      <Field label={T.strategy.description}>
        <textarea
          value={fields.text}
          onChange={e => onFieldsChange({ text: e.target.value })}
          placeholder={T.strategy.descriptionPlaceholder}
          rows={3}
          className={`${editorInput} resize-none`}
        />
      </Field>

      {/* Member dropdown — rendered via portal to escape overflow clipping */}
      {showMemberDropdown && memberOptions.length > 0 && createPortal(
        <div
          data-member-dropdown
          style={{
            position: 'fixed',
            top: memberDropdownPos.top,
            left: memberDropdownPos.left,
            width: memberDropdownPos.width,
            maxHeight: 280,
            zIndex: 9999,
          }}
          className={`overflow-y-auto ${editorMenu}`}
        >
          {filteredMembers.length === 0 && memberSearch && (
            <div className="px-3 py-2 text-xs text-foreground/40">{T.strategy.noMembersFound}</div>
          )}
          {filteredMembers.map(m => (
            <button
              key={m.id}
              onClick={() => handleAddMember(m)}
              className="flex w-full cursor-pointer items-center gap-2 px-2 py-1.5 text-xs text-foreground transition-colors hover:bg-white/10"
            >
              <img
                src={m.avatar_url}
                alt={m.display_name || m.username}
                className="h-5 w-5 rounded-full"
                onError={e => { (e.target as HTMLImageElement).src = '/default-avatar.png' }}
              />
              <span className="truncate">{m.display_name || m.username}</span>
              <span className="text-foreground/30 ml-auto">{m.team}</span>
            </button>
          ))}
        </div>,
        document.body
      )}

      {/* Squad dropdown — rendered via portal to escape overflow clipping */}
      {showSquadDropdown && squadPresets && squadPresets.length > 0 && createPortal(
        <div
          data-squad-dropdown
          style={{
            position: 'fixed',
            top: squadDropdownPos.top,
            left: squadDropdownPos.left,
            width: squadDropdownPos.width,
            maxHeight: 240,
            zIndex: 9999,
          }}
          className={`overflow-y-auto ${editorMenu}`}
        >
          {squadPresets.map(sp => {
            const isSelected = fields.squads.some(s => isSameSquad(s, sp))
            return (
              <button
                key={sp.id ?? sp.name}
                onClick={() => { handleToggleSquadPreset(sp); setShowSquadDropdown(false) }}
                className="flex w-full cursor-pointer items-center gap-2 px-2 py-1.5 text-xs text-foreground transition-colors hover:bg-white/10"
              >
                <div
                  className="h-4 w-4 shrink-0 rounded-sm border border-white/20"
                  style={{ backgroundColor: sp.color }}
                />
                <span className="flex-1 text-left truncate">{sp.name}</span>
                {sp.players && <span className="shrink-0 text-foreground/40">{sp.players.length}</span>}
                {isSelected && <Check className="h-3.5 w-3.5 text-primary shrink-0" />}
              </button>
            )
          })}
        </div>,
        document.body
      )}
    </>
  )
}
